import React from "react";
import dayjs from "dayjs";
import {
  Avatar,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { ProtectedPaths } from "routes";
import { green, grey, indigo, red } from "@mui/material/colors";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { useNavigate } from "react-router-dom";
import {
  exportNordPoolSupplyPlan,
  generateNordPoolSupplyPlans,
  exportNordPoolSupplyPlanResults,
  submitNordPoolSupplyPlan,
} from "api/resources/nordPool";
import { downloadXLSX } from "helpers/excelExport";
import { SupplyPlan } from "api/models/NordPool";
import LoadingButton from "@mui/lab/LoadingButton";
import { hasNPManualSubmitWindowClosed } from "helpers/auctions";

interface AuctionSupplyPlansTableProps {
  supplyPlans: SupplyPlan[];
  loading?: boolean;
  dispatch: any;
  setSnackbar?: (snackbar: { type: string; message: string }) => void;
}

const AuctionSupplyPlansTable = ({
  supplyPlans,
  loading,
  dispatch,
  setSnackbar,
}: AuctionSupplyPlansTableProps) => {
  const [regenerating, setRegenerating] = React.useState(null);
  const [exportingResults, setExportingResults] = React.useState(null);
  const [submitting, setSubmitting] = React.useState<number | null>(null);
  const navigate = useNavigate();

  const statuses = {
    PENDING: {
      color: grey[400],
      icon: <HourglassTopIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "In queue",
    },
    IN_PROGRESS: {
      color: indigo[400],
      icon: <HourglassTopIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Generating...",
    },
    COMPLETED: {
      color: grey[500],
      icon: <CheckIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Generated",
    },
    SENT_TO_NORDPOOL: {
      color: indigo[400],
      icon: <ArrowOutwardIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Sent",
    },
    ACCEPTED: {
      color: green[400],
      icon: <ArrowOutwardIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Accepted",
    },
    RESULTS_RECEIVED: {
      color: green[400],
      icon: <CheckIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Results",
    },
    ERROR: {
      color: red[400],
      icon: <CloseIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Error",
    },
    LIMITS_EXCEEDED: {
      color: red[400],
      icon: <PriorityHighIcon sx={{ width: 16, height: 16 }} />,
      tooltip: "Error",
    },
  };

  const handleExportSupplyPlan = (supplyPlan: any) => {
    exportNordPoolSupplyPlan(Number(supplyPlan.id))
      .then((result) => {
        downloadXLSX(
          result,
          `supply-plan_${supplyPlan.targetDate}_${supplyPlan.nordpoolAuctionTypeName}_${supplyPlan.portfolioName}.xlsx`,
        );
      })
      .catch((error) => console.error(error));
  };

  const handleRegenerateSupplyPlan = (
    date: string,
    auctionTypeId: number,
    portfolioId: number,
  ) => {
    setRegenerating(date + "-" + auctionTypeId + "-" + portfolioId);
    generateNordPoolSupplyPlans(date, auctionTypeId, portfolioId)
      .then(() => {
        setRegenerating(null);
        dispatch();
      })
      .catch((error) => {
        setRegenerating(null);
        console.error(error);
      });
  };

  const handleSubmitToNordPool = (supplyPlan: any) => {
    setSubmitting(supplyPlan.id);
    submitNordPoolSupplyPlan(Number(supplyPlan.id))
      .then(() => {
        setSubmitting(null);
        dispatch();
        setSnackbar?.({
          type: "success",
          message: "Supply plan sent to Nord Pool",
        });
      })
      .catch((error) => {
        setSubmitting(null);
        console.error(error);
        setSnackbar?.({
          type: "error",
          message:
            error.response?.data?.message ||
            error.message ||
            "Failed to send supply plan to Nord Pool",
        });
      });
  };

  const handleExportSupplyPlanResults = (supplyPlan: any) => {
    setExportingResults(supplyPlan.id);

    exportNordPoolSupplyPlanResults(Number(supplyPlan.id))
      .then((result) => {
        downloadXLSX(
          result,
          `supply-plan_results_${supplyPlan.targetDate}_${supplyPlan.nordpoolAuctionTypeName}_${supplyPlan.portfolioName}.xlsx`,
        );
        setExportingResults(null);
      })
      .catch((error) => {
        console.error(error);
        setExportingResults(null);
      });
  };

  return (
    <TableContainer sx={{ borderTop: `1px solid ${grey[400]}` }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">Portfolio</TableCell>
            <TableCell align="center">Min (MW)</TableCell>
            <TableCell align="center">Max (MW)</TableCell>
            <TableCell align="center">Sum Min (MW)</TableCell>
            <TableCell align="center">Sum Max (MW)</TableCell>
            <TableCell align="center">Created at</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {supplyPlans.map((supplyPlan: any, index: number) => {
            const disabled =
              regenerating ===
              supplyPlan.targetDate +
                "-" +
                supplyPlan.nordpoolAuctionTypeId +
                "-" +
                supplyPlan.portfolioId;

            return (
              <TableRow
                key={index}
                sx={{
                  ...(loading && { opacity: "0.3" }),
                  "&:last-child td, &:last-child th": {
                    border: 0,
                  },
                }}
              >
                <TableCell align="left">{supplyPlan.portfolioName}</TableCell>
                <TableCell align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography component="p" fontSize={14}>
                      {supplyPlan.currentAuctionMetrics.min}
                    </Typography>
                    <Typography
                      component="p"
                      color={grey[500]}
                      fontSize={14}
                      sx={{ ml: 0.6 }}
                    >
                      {supplyPlan.previousAuctionMetrics.min
                        ? "(" + supplyPlan.previousAuctionMetrics.min + ")"
                        : ""}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography component="p" fontSize={14}>
                      {supplyPlan.currentAuctionMetrics.max}
                    </Typography>
                    <Typography
                      component="p"
                      color={grey[500]}
                      fontSize={14}
                      sx={{ ml: 0.6 }}
                    >
                      {supplyPlan.previousAuctionMetrics.max
                        ? "(" + supplyPlan.previousAuctionMetrics.max + ")"
                        : ""}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography component="p" fontSize={14}>
                      {supplyPlan.currentAuctionMetrics.sumMin}
                    </Typography>
                    <Typography
                      component="p"
                      color={grey[500]}
                      fontSize={14}
                      sx={{ ml: 0.6 }}
                    >
                      {supplyPlan.previousAuctionMetrics.sumMin
                        ? "(" + supplyPlan.previousAuctionMetrics.sumMin + ")"
                        : ""}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography component="p" fontSize={14}>
                      {supplyPlan.currentAuctionMetrics.sumMax}
                    </Typography>
                    <Typography
                      component="p"
                      color={grey[500]}
                      fontSize={14}
                      sx={{ ml: 0.6 }}
                    >
                      {supplyPlan.previousAuctionMetrics.sumMax
                        ? "(" + supplyPlan.previousAuctionMetrics.sumMax + ")"
                        : ""}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {supplyPlan.createdAt
                    ? dayjs(supplyPlan.createdAt).format("DD.MM.YYYY HH:mm")
                    : ""}
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={statuses[supplyPlan.status || "GENERATED"]?.tooltip}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        margin: "0 auto",
                        bgcolor:
                          statuses[supplyPlan.status || "GENERATED"]?.color,
                      }}
                    >
                      {statuses[supplyPlan.status || "GENERATED"]?.icon}
                    </Avatar>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {supplyPlan.status === "PENDING" ||
                  supplyPlan.status === "IN_PROGRESS" ? (
                    <p>{statuses[supplyPlan.status]?.tooltip}</p>
                  ) : (
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/${ProtectedPaths.NordPoolSupplyPlans}/${supplyPlan.id}`,
                          )
                        }
                        disabled={disabled}
                      >
                        Open
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleExportSupplyPlan(supplyPlan)}
                        disabled={disabled}
                      >
                        Export
                      </Button>
                      <LoadingButton
                        variant="text"
                        size="small"
                        loading={disabled}
                        loadingIndicator="Generating..."
                        onClick={() =>
                          handleRegenerateSupplyPlan(
                            supplyPlan.targetDate,
                            supplyPlan.nordpoolAuctionTypeId,
                            supplyPlan.portfolioId,
                          )
                        }
                        disabled={disabled}
                      >
                        Regenerate
                      </LoadingButton>
                      {(supplyPlan.status === "COMPLETED" ||
                        supplyPlan.status === "ERROR" ||
                        supplyPlan.status === "LIMITS_EXCEEDED") &&
                        !hasNPManualSubmitWindowClosed(
                          supplyPlan.nordpoolAuctionTypeName,
                          dayjs(supplyPlan.targetDate),
                        ) && (
                        <LoadingButton
                          size="small"
                          variant="contained"
                          color="primary"
                          loading={submitting === supplyPlan.id}
                          loadingIndicator="Sending..."
                          onClick={() => handleSubmitToNordPool(supplyPlan)}
                          disabled={submitting === supplyPlan.id || disabled}
                        >
                          Send to Nord Pool
                        </LoadingButton>
                      )}
                      <LoadingButton
                        size="small"
                        variant="contained"
                        color="info"
                        loading={exportingResults === supplyPlan.id}
                        loadingIndicator="Exporting"
                        onClick={() =>
                          handleExportSupplyPlanResults(supplyPlan)
                        }
                        disabled={
                          exportingResults === supplyPlan.id ||
                          supplyPlan.status !== "RESULTS_RECEIVED"
                        }
                      >
                        Results
                      </LoadingButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AuctionSupplyPlansTable;
